/**
 * Типы для системы уведомлений Event Master
 * Фаза 2: Система уведомлений и real-time (Неделя 7)
 */

// Типы уведомлений согласно roadmap (СИНХРОНИЗИРОВАНО с backend shared-types.ts)
export enum NotificationType {
  SYSTEM = "system",
  EVENT = "event",
  TASK = "task",
  USER = "user",
  COMMENT = "comment",
  REFERRAL = "referral",
  ACHIEVEMENT = "achievement",
  PROMOTION = "promotion",
  REMINDER = "reminder",
  SOCIAL = "social",
  SECURITY = "security",
  // Новые типы для аналитики и AI (добавлено 15.01.2025)
  ANALYTICS_REPORT_READY = "analytics_report_ready",
  ANALYTICS_ANOMALY_DETECTED = "analytics_anomaly_detected",
  ML_MODEL_TRAINED = "ml_model_trained",
  ML_PREDICTION_READY = "ml_prediction_ready",
  AI_SUGGESTION_AVAILABLE = "ai_suggestion_available",
  DASHBOARD_SHARED = "dashboard_shared",
  WIDGET_UPDATE = "widget_update",
  PERFORMANCE_ALERT = "performance_alert",
  BUDGET_ALERT = "budget_alert",
  ATTENDANCE_FORECAST = "attendance_forecast",
  SMART_RECOMMENDATION = "smart_recommendation",
  SYSTEM_MAINTENANCE = "system_maintenance",
  FEATURE_UPDATE = "feature_update",
  INTEGRATION_STATUS = "integration_status",
}

// Каналы доставки (СИНХРОНИЗИРОВАНО с backend)
export enum NotificationChannel {
  IN_APP = "in_app",
  EMAIL = "email",
  PUSH = "push",
  SMS = "sms",
  TELEGRAM = "telegram",
  WEBHOOK = "webhook",
  SLACK = "slack",
}

// Приоритеты уведомлений (СИНХРОНИЗИРОВАНО с backend)
export enum NotificationPriority {
  LOW = "low",
  MEDIUM = "medium",
  HIGH = "high",
  URGENT = "urgent",
  CRITICAL = "critical",
}

// Статусы уведомлений
export enum NotificationStatus {
  UNREAD = "unread",
  READ = "read",
  ARCHIVED = "archived",
  DELETED = "deleted",
}

// Основной интерфейс уведомления
export interface INotification {
  id: string;
  userId: string;
  type: NotificationType;
  title: string;
  content: string;
  linkUrl?: string;
  /**
   * Полезная нагрузка уведомления ровно в том виде, в каком её отдаёт сервер:
   * колонка `data` (`jsonb`) сущности `Notification`. Здесь живут `linkUrl`,
   * `eventId`, `entityType` и прочее — поля объявлены отдельными наверху, но
   * сервер их отдельно НЕ шлёт (AD-150). Тип `unknown` намеренный: содержимое
   * `jsonb` ничем не гарантировано и требует проверки на месте чтения.
   */
  data?: Record<string, unknown> | null;
  metadata?: Record<string, any>;
  channels: NotificationChannel[];
  priority: NotificationPriority;
  status: NotificationStatus;
  isRead: boolean;
  isActive: boolean;
  readAt?: Date;
  expiresAt?: Date;
  entityId?: string;
  entityType?: string;
  createdAt: Date;
  updatedAt: Date;
}

// Настройки уведомлений пользователя
export interface INotificationSettings {
  id: string;
  userId: string;
  type: NotificationType;
  channels: NotificationChannel[];
  isEnabled: boolean;
  doNotDisturbStart?: string; // HH:mm формат
  doNotDisturbEnd?: string; // HH:mm формат
  grouping: boolean;
  batching: boolean;
  batchInterval: number; // минуты
  preferences: Record<string, any>;
  createdAt: Date;
  updatedAt: Date;
}

// Групповые уведомления
export interface INotificationGroup {
  id: string;
  type: NotificationType;
  count: number;
  latestNotification: INotification;
  notifications: INotification[];
  isExpanded: boolean;
}

// Статистика уведомлений
export interface INotificationStats {
  total: number;
  unread: number;
  byType: Record<NotificationType, number>;
  byPriority: Record<NotificationPriority, number>;
  engagement: {
    openRate: number;
    clickRate: number;
    avgTimeToRead: number;
  };
}

// Фильтры для уведомлений
export interface INotificationFilters {
  types?: NotificationType[];
  status?: NotificationStatus[];
  priority?: NotificationPriority[];
  dateRange?: {
    start: Date;
    end: Date;
  };
  search?: string;
  entityType?: string;
}

// Дополнительный интерфейс для API фильтров
export interface NotificationAPIFilters {
  types?: NotificationType[];
  status?: NotificationStatus; // Один статус, а не массив
  priority?: NotificationPriority[];
  dateRange?: {
    start: Date;
    end: Date;
  };
  search?: string;
  entityType?: string;
}

// Дополнительный интерфейс для API фильтров
export interface NotificationAPIFilters {
  types?: NotificationType[];
  status?: NotificationStatus; // Один статус, а не массив
  priority?: NotificationPriority[];
  dateRange?: {
    start: Date;
    end: Date;
  };
  search?: string;
  entityType?: string;
}

// Настройки сортировки
export interface INotificationSort {
  field: "createdAt" | "priority" | "type" | "status";
  order: "asc" | "desc";
}

// Пагинация
export interface INotificationPagination {
  page: number;
  limit: number;
  total: number;
  hasMore: boolean;
}

// WebSocket события
export enum NotificationSocketEvent {
  NOTIFICATION_RECEIVED = "notification:received",
  NOTIFICATION_READ = "notification:read",
  NOTIFICATION_DELETED = "notification:deleted",
  NOTIFICATION_SETTINGS_UPDATED = "notification:settings_updated",
  CONNECTION_STATUS = "notification:connection_status",
}

// Данные WebSocket события
export interface INotificationSocketData {
  event: NotificationSocketEvent;
  data: any;
  timestamp: Date;
}

// Состояние подключения
export enum ConnectionStatus {
  CONNECTING = "connecting",
  CONNECTED = "connected",
  DISCONNECTED = "disconnected",
  RECONNECTING = "reconnecting",
  ERROR = "error",
}

// Toast уведомления
export interface IToastNotification {
  id: string;
  type: "success" | "error" | "warning" | "info";
  title: string;
  message: string;
  duration?: number;
  actions?: {
    label: string;
    action: () => void;
    variant?: "text" | "outlined" | "contained";
  }[];
  persistent?: boolean;
  position?: "top-left" | "top-right" | "bottom-left" | "bottom-right";
}

// API ответы
export interface INotificationResponse {
  notifications: INotification[];
  pagination: INotificationPagination;
  stats: INotificationStats;
  unreadCount: number;
  // Контракт backend (ADR-NOTIFICATIONS): camelCase envelope (hasMore/unreadCount).
  hasMore: boolean;
  total: number;
}

export interface INotificationSettingsResponse {
  settings: INotificationSettings[];
}

// Действия с уведомлениями
export interface INotificationAction {
  id: string;
  label: string;
  action: (notification: INotification) => void;
  icon?: string;
  variant?: "primary" | "secondary" | "danger";
  condition?: (notification: INotification) => boolean;
}

// Шаблоны уведомлений
export interface INotificationTemplate {
  id: string;
  type: NotificationType;
  title: string;
  content: string;
  variables: string[];
  channels: NotificationChannel[];
  priority: NotificationPriority;
  expirationHours?: number;
}

// Конфигурация системы уведомлений
export interface INotificationConfig {
  maxRetries: number;
  retryDelay: number;
  batchSize: number;
  cacheTimeout: number;
  websocketUrl: string;
  enableOfflineQueue: boolean;
  enableGrouping: boolean;
  enableBatching: boolean;
  defaultChannel: NotificationChannel;
  defaultPriority: NotificationPriority;
}

// WebSocket события для real-time уведомлений
export interface WebSocketEvent {
  type: string;
  data: any;
  timestamp?: string;
}

// Алиас для совместимости
export type Notification = INotification;
